import { useCallback, useEffect, useState } from 'react';

import {
  fetchAdminDashboard,
  fetchAdminInscriptions,
  fetchAdminApprenantDetail,
  fetchAdminApprenants,
  fetchBusinessSettings,
  fetchAdminCohortes,
  fetchAdminSalles,
  fetchAdminVacations,
  createAdminCohorte,
  patchAdminCohorte,
  patchAdminSalle,
  fetchAdminPlanningGrid,
  fetchAdminChargeHoraireGrille,
  putAdminChargeHoraireAffectation,
  fetchAdminSalleAvailability,
  fetchAdminFormateurScheduleConflicts,
  fetchAdminFormateurs,
  fetchAdminFilieres,
  patchPlanningSession,
  createPlanningSession,
  deletePlanningSession,
  regeneratePlanningGrid,
  fetchFilieres,
  fetchAdminDomaines,
  createAdminDomaine,
  patchAdminDomaine,
  createAdminFiliere,
  patchAdminFiliere,
  createAdminSalle,
  fetchFiliereCapaciteSuggestion,
  fetchTarifs,
  fetchAdminTarifFormateur,
  patchAdminTarifFormation,
  patchAdminTarifFormateur,
  fetchAdminParrainageSettings,
  patchAdminParrainageSettings,
  patchAdminParrainageRegle,
  patchCurrencySettings,
} from '../services/academiaAdminService';

export function useAcademiaAdminDashboard() {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const refetch = useCallback(async () => {
    try {
      setIsError(false);
      setIsLoading(true);
      const next = await fetchAdminDashboard();
      setData(next);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { data, isLoading, isError, refetch };
}

export function useAcademiaAdminInscriptions() {
  const [enAttente, setEnAttente] = useState([]);
  const [actives, setActives] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const refetch = useCallback(async () => {
    try {
      setIsError(false);
      setIsLoading(true);
      const next = await fetchAdminInscriptions();
      setEnAttente(next.enAttente ?? []);
      setActives(next.actives ?? []);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { enAttente, actives, isLoading, isError, refetch };
}

export function useAcademiaTarifs() {
  const [items, setItems] = useState([]);
  const [formateur, setFormateur] = useState(null);
  const [parrainage, setParrainage] = useState({ bonusInscriptionCdf: 0, regles: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastVariant, setToastVariant] = useState(/** @type {'success' | 'error'} */ ('success'));

  const refetch = useCallback(async () => {
    try {
      setIsError(false);
      setIsLoading(true);
      const [formation, formateurRow, parrainageSettings] = await Promise.all([
        fetchTarifs(),
        fetchAdminTarifFormateur().catch(() => null),
        fetchAdminParrainageSettings().catch(() => ({ bonusInscriptionCdf: 0, regles: [] })),
      ]);
      setItems(formation);
      setFormateur(formateurRow);
      setParrainage(parrainageSettings);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const saveFormationTarif = useCallback(
    async (id, payload) => {
      setIsSaving(true);
      setToastMessage(null);
      try {
        await patchAdminTarifFormation(id, payload);
        await refetch();
        setToastVariant('success');
        setToastMessage('Tarif enregistré.');
      } catch (e) {
        setToastVariant('error');
        setToastMessage(e instanceof Error ? e.message : 'Enregistrement impossible.');
        throw e;
      } finally {
        setIsSaving(false);
      }
    },
    [refetch],
  );

  const saveFormateurTarif = useCallback(
    async (id, payload) => {
      setIsSaving(true);
      setToastMessage(null);
      try {
        await patchAdminTarifFormateur(id, payload);
        await refetch();
        setToastVariant('success');
        setToastMessage('Tarif formateur enregistré.');
      } catch (e) {
        setToastVariant('error');
        setToastMessage(e instanceof Error ? e.message : 'Enregistrement impossible.');
        throw e;
      } finally {
        setIsSaving(false);
      }
    },
    [refetch],
  );

  const saveParrainageBonus = useCallback(
    async (bonusInscriptionCdf) => {
      setIsSaving(true);
      setToastMessage(null);
      try {
        const data = await patchAdminParrainageSettings({ bonusInscriptionCdf });
        setParrainage(data);
        setToastVariant('success');
        setToastMessage('Bonus parrainage enregistré.');
      } catch (e) {
        setToastVariant('error');
        setToastMessage(e instanceof Error ? e.message : 'Enregistrement impossible.');
        throw e;
      } finally {
        setIsSaving(false);
      }
    },
    [],
  );

  const saveParrainageRegle = useCallback(
    async (id, payload) => {
      setIsSaving(true);
      setToastMessage(null);
      try {
        await patchAdminParrainageRegle(id, payload);
        await refetch();
        setToastVariant('success');
        setToastMessage('Règle de parrainage enregistrée.');
      } catch (e) {
        setToastVariant('error');
        setToastMessage(e instanceof Error ? e.message : 'Enregistrement impossible.');
        throw e;
      } finally {
        setIsSaving(false);
      }
    },
    [refetch],
  );

  const clearToast = useCallback(() => setToastMessage(null), []);

  return {
    items,
    formateur,
    parrainage,
    isLoading,
    isError,
    isSaving,
    toastMessage,
    toastVariant,
    clearToast,
    refetch,
    saveFormationTarif,
    saveFormateurTarif,
    saveParrainageBonus,
    saveParrainageRegle,
  };
}

export function useAcademiaFormations() {
  const [filieres, setFilieres] = useState([]);
  const [cohortes, setCohortes] = useState([]);
  const [selectedFiliereId, setSelectedFiliereId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const loadFilieres = useCallback(async () => {
    const next = await fetchAdminFilieres();
    setFilieres(next);
    if (next.length > 0 && !selectedFiliereId) {
      setSelectedFiliereId(next[0].id);
    }
  }, [selectedFiliereId]);

  const loadCohortes = useCallback(async () => {
    const next = await fetchAdminCohortes();
    setCohortes(next);
  }, []);

  const refetch = useCallback(async () => {
    try {
      setIsError(false);
      setIsLoading(true);
      await Promise.all([loadFilieres(), loadCohortes()]);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [loadFilieres, loadCohortes]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return {
    filieres,
    cohortes,
    selectedFiliereId,
    setSelectedFiliereId,
    isLoading,
    isError,
    refetch,
    reloadCohortes: loadCohortes,
  };
}

export function useAcademiaAdminDomaines() {
  const [domaines, setDomaines] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const refetch = useCallback(async () => {
    try {
      setIsError(false);
      setIsLoading(true);
      const rows = await fetchAdminDomaines();
      setDomaines(Array.isArray(rows) ? rows : []);
    } catch {
      setIsError(true);
      setDomaines([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { domaines, isLoading, isError, refetch };
}

export function useAcademiaAdminDomaineMutations(reloadDomaines) {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const createDomaine = useCallback(
    async (payload) => {
      setIsSaving(true);
      setError(null);
      try {
        await createAdminDomaine(payload);
        await reloadDomaines();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Échec de la création');
        throw e;
      } finally {
        setIsSaving(false);
      }
    },
    [reloadDomaines],
  );

  const updateDomaine = useCallback(
    async (id, payload) => {
      setIsSaving(true);
      setError(null);
      try {
        await patchAdminDomaine(id, payload);
        await reloadDomaines();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Échec de la mise à jour');
        throw e;
      } finally {
        setIsSaving(false);
      }
    },
    [reloadDomaines],
  );

  return { createDomaine, updateDomaine, isSaving, error };
}

export function useAcademiaAdminSalles() {
  const [salles, setSalles] = useState([]);
  const [vacations, setVacations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const refetch = useCallback(async () => {
    try {
      setIsError(false);
      setIsLoading(true);
      const [s, v] = await Promise.all([fetchAdminSalles(), fetchAdminVacations()]);
      setSalles(s);
      setVacations(v);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const saveSalle = useCallback(
    async (id, payload) => {
      await patchAdminSalle(id, payload);
      await refetch();
    },
    [refetch],
  );

  const createSalle = useCallback(
    async (payload) => {
      await createAdminSalle(payload);
      await refetch();
    },
    [refetch],
  );

  return { salles, vacations, isLoading, isError, refetch, saveSalle, createSalle };
}

export function useAcademiaAdminFiliereMutations(reloadFilieres) {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const createFiliere = useCallback(
    async (payload) => {
      setIsSaving(true);
      setError(null);
      try {
        await createAdminFiliere(payload);
        await reloadFilieres();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Échec de la création');
        throw e;
      } finally {
        setIsSaving(false);
      }
    },
    [reloadFilieres],
  );

  const updateFiliere = useCallback(
    async (id, payload) => {
      setIsSaving(true);
      setError(null);
      try {
        await patchAdminFiliere(id, payload);
        await reloadFilieres();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Échec de la mise à jour');
        throw e;
      } finally {
        setIsSaving(false);
      }
    },
    [reloadFilieres],
  );

  return { createFiliere, updateFiliere, isSaving, error };
}

export function useAcademiaAdminCohorteMutations(selectedFiliereId, reloadCohortes) {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const createCohorte = useCallback(
    async (payload) => {
      setIsSaving(true);
      setError(null);
      try {
        await createAdminCohorte(payload);
        if (selectedFiliereId) await reloadCohortes(selectedFiliereId);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Échec de la création');
        throw e;
      } finally {
        setIsSaving(false);
      }
    },
    [selectedFiliereId, reloadCohortes],
  );

  const updateCohorte = useCallback(
    async (id, payload) => {
      setIsSaving(true);
      setError(null);
      try {
        await patchAdminCohorte(id, payload);
        if (selectedFiliereId) await reloadCohortes(selectedFiliereId);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Échec de la mise à jour');
        throw e;
      } finally {
        setIsSaving(false);
      }
    },
    [selectedFiliereId, reloadCohortes],
  );

  return { createCohorte, updateCohorte, isSaving, error };
}

export function useAcademiaPlanning() {
  const [grid, setGrid] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [formateurConflicts, setFormateurConflicts] = useState({
    totalConflicts: 0,
    formateursEnConflit: 0,
    conflicts: [],
  });
  const [cohortes, setCohortes] = useState([]);
  const [formateurs, setFormateurs] = useState([]);
  const [filieres, setFilieres] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [selectedJour, setSelectedJour] = useState('LUNDI');

  const refetch = useCallback(async () => {
    try {
      setIsError(false);
      setIsLoading(true);
      const [g, a, fc, c, f, fil] = await Promise.all([
        fetchAdminPlanningGrid(),
        fetchAdminSalleAvailability(),
        fetchAdminFormateurScheduleConflicts(),
        fetchAdminCohortes(),
        fetchAdminFormateurs(),
        fetchAdminFilieres(),
      ]);
      setGrid(g);
      setAvailability(a);
      setFormateurConflicts(fc);
      setCohortes(c);
      setFormateurs(f);
      setFilieres(fil);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const assignCohorte = useCallback(
    async (sessionId, idCohorte) => {
      await patchPlanningSession(sessionId, { idCohorte: idCohorte || null });
      await refetch();
    },
    [refetch],
  );

  const assignFormateur = useCallback(
    async (sessionId, idFormateur) => {
      await patchPlanningSession(sessionId, { idFormateur: idFormateur || null });
      await refetch();
    },
    [refetch],
  );

  const assignDateSeance = useCallback(
    async (sessionId, dateSeance, justificationDateSeance = null) => {
      await patchPlanningSession(sessionId, {
        dateSeance: dateSeance || null,
        justificationDateSeance: justificationDateSeance || null,
      });
      await refetch();
    },
    [refetch],
  );

  const createSessionOnSlot = useCallback(
    async (payload) => {
      await createPlanningSession(payload);
      await refetch();
    },
    [refetch],
  );

  const deleteSessionById = useCallback(
    async (sessionId) => {
      await deletePlanningSession(sessionId);
      await refetch();
    },
    [refetch],
  );

  const regenerateGrid = useCallback(async () => {
    await regeneratePlanningGrid();
    await refetch();
  }, [refetch]);

  return {
    grid,
    availability,
    formateurConflicts,
    cohortes,
    formateurs,
    filieres,
    selectedJour,
    setSelectedJour,
    isLoading,
    isError,
    refetch,
    assignCohorte,
    assignFormateur,
    assignDateSeance,
    createSessionOnSlot,
    deleteSessionById,
    regenerateGrid,
  };
}

export function useAcademiaCurrencySettings() {
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastVariant, setToastVariant] = useState('success');

  const refetch = useCallback(async () => {
    try {
      setIsError(false);
      const data = await fetchBusinessSettings();
      setSettings(data);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const saveCurrency = useCallback(async (payload) => {
    setIsSaving(true);
    try {
      await patchCurrencySettings(payload);
      await refetch();
      setToastVariant('success');
      setToastMessage('Paramètres de devise enregistrés.');
    } catch (error) {
      setToastVariant('error');
      setToastMessage(error instanceof Error ? error.message : 'Échec de l’enregistrement.');
    } finally {
      setIsSaving(false);
    }
  }, [refetch]);

  return {
    settings,
    isLoading,
    isError,
    isSaving,
    toastMessage,
    toastVariant,
    clearToast: () => setToastMessage(null),
    refetch,
    saveCurrency,
  };
}

export function useAcademiaApprenants() {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const refetch = useCallback(async () => {
    try {
      setIsError(false);
      setIsLoading(true);
      const next = await fetchAdminApprenants();
      setItems(next);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { items, isLoading, isError, refetch };
}

export function useAdminApprenantDetail(apprenantId) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const refetch = useCallback(async () => {
    if (!apprenantId) {
      setData(null);
      setIsLoading(false);
      setIsError(true);
      setErrorMessage('Identifiant apprenant manquant.');
      return;
    }
    try {
      setIsError(false);
      setErrorMessage(null);
      setIsLoading(true);
      const next = await fetchAdminApprenantDetail(apprenantId);
      setData(next);
    } catch (error) {
      setIsError(true);
      setData(null);
      const message = error instanceof Error ? error.message : 'Impossible de charger la fiche apprenant.';
      setErrorMessage(
        message.includes('Unexpected token') || message.includes('not valid JSON')
          ? 'Le serveur API ne répond pas correctement (route absente ou backend à redémarrer après mise à jour).'
          : message,
      );
    } finally {
      setIsLoading(false);
    }
  }, [apprenantId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { data, isLoading, isError, errorMessage, refetch };
}

export function useAcademiaChargeHoraire() {
  const [grille, setGrille] = useState(null);
  const [formateurs, setFormateurs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [selectedJour, setSelectedJour] = useState('LUNDI');
  const [selectedFiliereId, setSelectedFiliereId] = useState('');

  const refetch = useCallback(async () => {
    try {
      setIsError(false);
      setIsLoading(true);
      const [g, f] = await Promise.all([
        fetchAdminChargeHoraireGrille(selectedJour),
        fetchAdminFormateurs(),
      ]);
      setGrille(g);
      setFormateurs(f);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [selectedJour]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const assignFormateurCohorte = useCallback(
    async (idCohorte, idFormateur) => {
      await putAdminChargeHoraireAffectation({
        idCohorte,
        idFormateur: idFormateur || null,
      });
      await refetch();
    },
    [refetch],
  );

  return {
    grille,
    formateurs,
    selectedJour,
    setSelectedJour,
    selectedFiliereId,
    setSelectedFiliereId,
    isLoading,
    isError,
    refetch,
    assignFormateurCohorte,
  };
}
